import { Divider, Flex } from '@chakra-ui/react';
import { faEnvelope, faHourglass } from '@fortawesome/pro-regular-svg-icons';
import HighlightItem from './HighlightItem';
import { IPeerReviewManagementListView } from '@publishing-engine/domain/views';
import { useState } from 'react';
import {
  IPeerReviewHighlightsCounts,
  IWorkflowData
} from '@publishing-engine/domain/models';
import {
  CompletedReviews,
  CriticalDeadlineReviews,
  ReviewsDeadlineRequested
} from '../common/HighlightsComponents';
import { EditorialAuthorWithdrawalRequested } from '../common/WithdrawalRequested';
import { ButtonText } from '@morressier/alembic';
import ProtectedWorkflowAction from 'src/components/ProtectedAction/ProtectedWorkflowAction';
import AuthorSubmissionFormDataProvider from '../../../../../contexts/SubmissionContextProvider';
import RsInviteModal from '../../../ReviewTab/ReviewerSearch';
import { useQuery } from '@tanstack/react-query';
import { fetchBlockedReviewers } from '../../../../../api/queries';
import { useSubmissionData } from '../../../../../contexts/SubmissionDataProvider';
import { ReviewersProvider } from '../../../../../contexts/ReviewersProvider';

interface HighlightsProps {
  reviews: IPeerReviewManagementListView | undefined;
  submissionId: string;
  workflowData: IWorkflowData;
  highlightSummaryCounts?: IPeerReviewHighlightsCounts;
}

const Highlights = ({
  reviews,
  submissionId,
  workflowData,
  highlightSummaryCounts
}: HighlightsProps) => {
  const [isInviteReviewerModalOpen, setIsInviteReviewerModalOpen] =
    useState(false);

  const minReviews = workflowData.minReviews;

  const totalReviews = reviews?.total ?? 0;
  const completedReviews = reviews?.reviewSummaryCounts?.completed || 0;
  const awaitingScoringReviews = highlightSummaryCounts?.awaitingScoring || 0;

  const criticalDeadlineReviews =
    highlightSummaryCounts?.approachingDeadline || 0;

  const expiredReviews = highlightSummaryCounts?.expired || 0;

  const deadlineExtensionRequests =
    highlightSummaryCounts?.extensionRequested || 0;

  const { submissionContextId: ctxId } = useSubmissionData();

  const reviewsQueryKey = [
    'submission-context',
    ctxId,
    submissionId,
    'reviews'
  ];

  const { data } = useQuery({
    queryKey: [...reviewsQueryKey, 'blocked-reviewers'],
    queryFn: () => (ctxId ? fetchBlockedReviewers(ctxId, submissionId) : null)
  });
  const blockedReviewers = data?.items || [];

  const NoReviewers = () =>
    totalReviews === 0 && (
      <>
        {isInviteReviewerModalOpen && (
          <AuthorSubmissionFormDataProvider>
            <ReviewersProvider>
              <RsInviteModal
                closeModal={() => setIsInviteReviewerModalOpen(false)}
                submissionId={submissionId}
                blockedReviewers={blockedReviewers}
              />
            </ReviewersProvider>
          </AuthorSubmissionFormDataProvider>
        )}

        <HighlightItem
          variant="attention"
          icon={faEnvelope}
          heading="No Reviewers Yet Invited"
          customButton={
            <ProtectedWorkflowAction actionName="RequestReview">
              <ButtonText
                flexShrink="0"
                onClick={() => setIsInviteReviewerModalOpen(true)}
              >
                Invite Reviewers
              </ButtonText>
            </ProtectedWorkflowAction>
          }
        />
        <Divider />
      </>
    );

  const AwaitingScoring = () =>
    awaitingScoringReviews > 0 && (
      <>
        <Divider />
        <HighlightItem
          variant="attention"
          icon={faHourglass}
          heading={`${awaitingScoringReviews} Completed Reviews Await Scoring`}
          context="Scoring completed reviews is advised before making a decision."
        />
      </>
    );

  return (
    <Flex
      flexDirection="column"
      borderRadius="lg"
      borderColor="neutralPrimaryBorder"
    >
      <EditorialAuthorWithdrawalRequested />
      <NoReviewers />
      <CompletedReviews
        minReviews={minReviews}
        completedReviews={completedReviews}
      />
      <AwaitingScoring />
      <ReviewsDeadlineRequested
        deadlineExtensionRequests={deadlineExtensionRequests}
      />
      <CriticalDeadlineReviews
        criticalDeadlineReviews={criticalDeadlineReviews}
        expiredReviews={expiredReviews}
      />
    </Flex>
  );
};

export default Highlights;
